import { GridFields, InfoCard } from 'components';
import { lodash } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { Chip, MenuOption } from 'libs/ui';
import { Icon } from 'libs/ui/Icon';
import { Colors } from 'libs/ui/theme';

import { BranchesItem, BranchOptionType } from './types';

export interface BranchesInfoCardProps {
  items: BranchesItem[];
  editable?: boolean;
  onCellClick: (row: BranchesItem) => void;
  onClickDelete?: (row: BranchesItem) => void;
  onClickArchive?: (row: BranchesItem) => void;
}

export const BranchesInfoCard = ({
  items,
  editable,
  onCellClick,
  onClickDelete,
  onClickArchive,
}: BranchesInfoCardProps) => {
  const { t } = useTranslation();

  const shownOptions: Partial<Record<BranchOptionType, boolean>> = {
    archive: !!onClickArchive,
    dearchive: !!onClickArchive,
    delete: !!onClickDelete,
  };

  const onClickMenuOption = (
    item: BranchesItem,
    option: MenuOption<BranchOptionType>
  ) => {
    switch (option.value) {
      case BranchOptionType.delete:
        if (item.id) {
          onClickDelete?.(item);
        }
        break;
      case BranchOptionType.archive:
      case BranchOptionType.dearchive:
        if (item.id) {
          onClickArchive?.(item);
        }
        break;
      default:
        break;
    }
  };

  return (
    <GridFields>
      {items.map((item) => (
        <InfoCard
          fields={[
            {
              label: t('client.legal.companyBranches.columns.status'),
              value: (
                <Chip
                  size="xs"
                  text={
                    item.archived
                      ? t('common.archivedStatus')
                      : t('common.activeStatus')
                  }
                  variant={item.archived ? 'gray' : 'green'}
                />
              ),
            },
            {
              label: t('client.legal.companyBranches.columns.country'),
              value: item.country,
            },
          ]}
          menuOptions={
            editable
              ? lodash.compact([
                  shownOptions.archive &&
                    !item.archived && {
                      value: BranchOptionType.archive,
                      label: t('common.archive'),
                      Icon: Icon.TimeSleep,
                    },
                  shownOptions.dearchive &&
                    item.archived && {
                      value: BranchOptionType.dearchive,
                      label: t('common.dearchive'),
                      Icon: Icon.RotateRightCheck,
                    },
                  shownOptions.delete && {
                    value: BranchOptionType.delete,
                    label: t('common.delete'),
                    Icon: Icon.Bucket,
                    color: Colors.red,
                  },
                ])
              : undefined
          }
          title={item.name}
          variant={item.archived ? 'gray' : 'default'}
          onClick={() => onCellClick(item)}
          onClickMenuOption={(option) => onClickMenuOption(item, option)}
        />
      ))}
    </GridFields>
  );
};
